import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SUBJECT_NAMES } from '@padcanvas/shared';
import { SearchFilterBar, DataTable, type FilterDef, type ColumnDef, type SortState } from '../components';
import { ViewToggle, type TableView } from '../responsive';
import type { SimulationListing, SimulationsSource } from './types';
import { LabMark } from '../lab-marks';
import './simulations.css';

/** The query keys this gallery owns. Anything else in the URL — the teacher
 *  app's `tab`, say — is not a filter and must survive a filter change. */
const FILTER_KEYS = ['search', 'subject', 'topic', 'status', 'moduleId'] as const;

/** Canonical display label for a subject key (maths → Maths). */
const label = (s: string) => SUBJECT_NAMES[s] ?? s;

/** Day precision — the time of day is never the question here, and a full
 *  timestamp would push the column wider than the answer is worth. */
const formatModified = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Each subject's place on the pH scale — zitmus's colours, so a card reads the
 *  same wherever it is drawn. */
const SUBJECT_CAP: Record<string, string> = {
  chemistry: '#c7343a',
  physics: '#2f6fcc',
  biology: '#3f9a5c',
  maths: '#e07a28',
};

interface Props<T extends SimulationListing> {
  source: SimulationsSource<T>;
  /**
   * The curator's view: a Status filter and column, a Modified column, and
   * drafts dimmed and badged. Admin sees every simulation and is deciding which
   * go live; a teacher is only ever sent live ones, so none of that means
   * anything there.
   */
  curation?: boolean;
  /** The heading — "Simulations" unless the surface shelves something else
   *  on these cards (the student app's guided experiments). */
  title?: string;
  /** The line under the heading — who the gallery is for is the surface's to say. */
  subtitle?: React.ReactNode;
  /** False where the page already carries the title — a tabbed surface, where
   *  the heading belongs to the tab strip rather than to one of its tabs. */
  heading?: boolean;
  /** Where a simulation's detail page lives. */
  basePath?: string;
  /** Where one entry opens, when it is not `${basePath}/${id}` — admin lists
   *  guided experiments beside the simulations, and an experiment opens in its
   *  editor rather than in the simulation player. */
  hrefFor?: (item: T) => string | undefined;
  /** localStorage key for the list view's hidden columns. */
  columnToggleKey: string;
}

/**
 * The simulations gallery — tiles or a list, searchable and filterable by
 * subject, topic and chapter. Shared by the admin console and the teacher app,
 * so both show the same catalogue the same way.
 */
export function SimulationsGallery<T extends SimulationListing>({
  source,
  curation = false,
  title = 'Simulations',
  subtitle = 'Interactive concept illustrations students can play with — drive the parameters and watch the physics respond.',
  heading = true,
  basePath = '/simulations',
  hrefFor,
  columnToggleKey,
}: Props<T>) {
  const navigate = useNavigate();
  const hrefOf = (item: T): string => hrefFor?.(item) ?? `${basePath}/${item.id}`;
  // One values map for the whole bar, as SearchFilterBar expects. Empty string
  // is "no constraint" for every filter, which is also its Clear state.
  // Filters in the URL so returning from a simulation detail restores them.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const k of FILTER_KEYS) { const v = searchParams.get(k); if (v) out[k] = v; }
    return out;
  }, [searchParams]);
  const setFilters = (next: Record<string, string>) =>
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      for (const k of FILTER_KEYS) { const v = next[k]; if (v) p.set(k, v); else p.delete(k); }
      return p;
    }, { replace: true });
  const [all, setAll] = useState<T[]>([]);
  const [modules, setModules] = useState<{ id: string; displayName: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tiles are the default: this is a gallery first, and a thumbnail is how a
  // reader recognises a sim. The list is the scanning view — denser, sortable.
  // Remembered per browser rather than in the URL, which carries the filters:
  // those describe *what* you are looking at and are worth sharing, the view
  // is a personal preference and would only add noise to a shared link.
  const [view, setView] = useState<TableView>(
    () => (localStorage.getItem('simulations-view') as TableView) || 'grid',
  );
  const changeView = (next: TableView) => {
    setView(next);
    localStorage.setItem('simulations-view', next);
  };
  // Title ascending is the order the tiles already flow in, so switching view
  // does not also reshuffle the list.
  const [sort, setSort] = useState<SortState>({ sortBy: 'title', sortOrder: 'asc' });

  const { search = '', subject = '', topic = '', moduleId = '' } = filters;
  // A status filter left in a URL means nothing where there is no status filter.
  const status = curation ? filters.status ?? '' : '';

  useEffect(() => {
    source
      .list()
      .then(setAll)
      .catch((e) => setError(e.message || 'Failed to load simulations'))
      .finally(() => setLoading(false));
  }, [source]);

  // Names for the module filter. Sims carry moduleIds only, and the list spans
  // every subject, so this is one unscoped fetch rather than one per subject.
  // Best-effort: a failure costs the module filter its labels, not the page.
  useEffect(() => {
    source
      .moduleNames()
      .then(setModules)
      .catch(() => setModules([]));
  }, [source]);

  const moduleName = useMemo(() => {
    const byId = new Map(modules.map((m) => [m.id, m]));
    // Fall back to the raw id so an association pointing at a deleted module is
    // still selectable rather than vanishing from the filter.
    return (id: string) => byId.get(id)?.displayName ?? id;
  }, [modules]);

  // The chapter, as zitmus labels its cards: it is what tells a reader where
  // this sits in what they are studying. The topic stands in when no chapter
  // name resolves, and a second fact (an experiment's length) rides beside it.
  const cardLabel = (sim: T): string => {
    const firstChapter = sim.moduleIds?.[0];
    const chapter = firstChapter && moduleName(firstChapter) !== firstChapter ? moduleName(firstChapter) : '';
    return [chapter || sim.topic, sim.detail].filter(Boolean).join(' · ');
  };

  // Topic and module options are scoped to the chosen subject — a physics topic
  // in a chemistry-filtered list is noise. Both are derived from the sims
  // themselves, so they only ever offer values that match something.
  const inSubject = useMemo(
    () => all.filter((s) => !subject || s.subject === subject),
    [all, subject],
  );

  const filterDefs = useMemo<FilterDef[]>(() => {
    const subjects = Array.from(new Set(all.map((s) => s.subject))).sort();

    const topics = Array.from(new Set(inSubject.map((s) => s.topic).filter(Boolean))).sort();

    // Only modules that actually have a sim, with a count. The full curriculum
    // runs to hundreds of modules and listing them all would bury the handful
    // that matter. Note this cannot answer the inverse — which modules have NO
    // sim — since those never appear.
    const moduleCounts = new Map<string, number>();
    for (const s of inSubject) {
      for (const id of s.moduleIds ?? []) moduleCounts.set(id, (moduleCounts.get(id) ?? 0) + 1);
    }
    const moduleOpts = Array.from(moduleCounts.entries())
      .map(([id, count]) => ({ value: id, label: `${moduleName(id)} (${count})` }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      {
        key: 'subject',
        placeholder: 'All subjects',
        options: subjects.map((s) => ({ value: s, label: label(s) })),
      },
      { key: 'topic', placeholder: 'All topics', options: topics.map((t) => ({ value: t, label: t })) },
      ...(curation ? [{
        key: 'status',
        placeholder: 'All statuses',
        // Live vs draft is the students-can-see-it line: only `live` sims are
        // returned by the student catalog (SimulationRepository.findLive).
        options: [{ value: 'live', label: 'Live' }, { value: 'draft', label: 'Draft' }],
      }] : []),
      { key: 'moduleId', placeholder: 'All chapters', options: moduleOpts },
    ];
  }, [all, inSubject, moduleName, curation]);

  // Topic and module options are scoped to the selected subject, so switching
  // subject can strand a selection that no longer exists — leaving a filter
  // that silently matches nothing. Clearing them here, in the change handler,
  // rather than in an effect that reacts to the new options: the dependency is
  // known at the moment of the change, so there is nothing to synchronize.
  const onFiltersChange = (next: Record<string, string>) => {
    setFilters(next.subject !== filters.subject ? { ...next, topic: '', moduleId: '' } : next);
  };

  const sims = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((s) =>
      (!subject || s.subject === subject)
      && (!topic || s.topic === topic)
      && (!status || s.status === status)
      && (!moduleId || (s.moduleIds ?? []).includes(moduleId))
      // Search spans the fields a reader would recall a sim by — its title, its
      // one-line hook, its topic, and the slug that names the asset file.
      && (!q
        || s.title.toLowerCase().includes(q)
        || s.tagline.toLowerCase().includes(q)
        || s.topic.toLowerCase().includes(q)
        || s.id.toLowerCase().includes(q)),
    );
  }, [all, search, subject, topic, status, moduleId]);

  const draft = (s: T) => curation && s.status === 'draft';

  // The list view's columns. The tiles are NOT DataTable's mobile card view:
  // that card has no room for a thumbnail, and the thumbnail is the whole
  // point of the gallery. So the two views render separately and share only
  // the data and the click target.
  const columns = useMemo<ColumnDef<T>[]>(() => [
    {
      key: 'thumb',
      header: '',
      width: '76px',
      // Decorative here — the title column beside it names the row.
      mobile: 'hide',
      hideable: false,
      render: (s) => (
        <span className="sim-row-thumb" aria-hidden="true">
          {s.thumbnailUrl
            ? <img src={s.thumbnailUrl} alt="" loading="lazy" />
            : s.mark
              ? <span className="sim-row-glyph"><LabMark lab={s.mark} size={28} /></span>
              : <span className="sim-row-glyph">{s.glyph}</span>}
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Simulation',
      mobile: 'title',
      hideable: false,
      sortable: true,
      sortValue: (s) => s.title,
      render: (s) => (
        <span className="sim-row-title">
          <span className="sim-row-name">{s.title}</span>
          <span className="sim-row-tagline">{s.tagline}</span>
        </span>
      ),
      // The card already gives the tagline its own line; in the mobile card the
      // title slot is a single line, so it would be truncated to nothing useful.
      mobileRender: (s) => s.title,
    },
    {
      key: 'subject',
      header: 'Subject',
      width: '110px',
      mobile: 'meta',
      sortable: true,
      sortValue: (s) => label(s.subject),
      render: (s) => label(s.subject),
    },
    {
      key: 'topic',
      header: 'Topic',
      width: '160px',
      mobile: 'meta',
      sortable: true,
      sortValue: (s) => s.topic,
      render: (s) => s.topic,
    },
    ...(curation ? [
      {
        key: 'updatedAt',
        header: 'Modified',
        width: '130px',
        mobile: 'meta',
        sortable: true,
        // The raw ISO string, not the formatted date: 'Feb 3' sorts before
        // 'Jan 9' alphabetically, which is not what anyone means by sorting a
        // date. Empty sorts last either way.
        sortValue: (s: T) => s.updatedAt ?? '',
        render: (s: T) => (
          <span className="sim-row-modified" title={s.updatedAt ?? ''}>
            {s.updatedAt ? formatModified(s.updatedAt) : '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: '90px',
        className: 'sim-td-status',
        mobile: 'accessory',
        sortable: true,
        // Locked. Draft-or-live is the reason the curation view exists, and a
        // hidden Status made the list look like the reader's gallery with a
        // column missing. Locking also overrides a stored preference that
        // hid it before this — those persist per device.
        hideable: false,
        sortValue: (s: T) => s.status,
        render: (s: T) => (s.status === 'draft'
          ? <span className="sim-card-draft">Draft</span>
          : <span className="sim-row-live">Live</span>),
      },
    ] as ColumnDef<T>[] : []),
  ], [curation]);

  const filtered = !!(search || subject || topic || status || moduleId);

  return (
    <div className="sim-page">
      {heading && (
        <div className="sim-header">
          <div>
            <h1>{title}</h1>
            <p className="sim-sub">{subtitle}</p>
          </div>
        </div>
      )}

      {/* hideFetch: the whole catalog is already in memory, so filtering is
          instant and a commit step would only add a click. */}
      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={onFiltersChange}
        searchPlaceholder={curation ? 'Search title, topic or slug…' : 'Search title or topic…'}
        hideFetch
        trailingActions={<ViewToggle view={view} onChange={changeView} />}
      />

      {loading ? (
        <div className="sim-empty">Loading…</div>
      ) : error ? (
        <div className="sim-empty">{error}</div>
      ) : sims.length === 0 ? (
        <div className="sim-empty">
          {filtered ? 'Nothing matches these filters.' : 'Nothing published yet.'}
        </div>
      ) : view === 'table' ? (
        <DataTable<T>
          columns={columns}
          data={sims}
          rowKey={(sim) => sim.id}
          onRowClick={(sim) => navigate(hrefOf(sim))}
          // cmd/middle-click opens the sim in a new tab instead of losing the
          // reader's place in a long list.
          rowHref={hrefOf}
          sort={sort}
          onSortChange={setSort}
          // Same signal the tiles carry: a draft is dimmed rather than badged,
          // so an unpublished row reads as unpublished at a glance.
          rowClassName={(sim) => (draft(sim) ? 'sim-row-unpublished' : '')}
          bordered
          mobileCards
          columnToggleKey={columnToggleKey}
          emptyMessage="Nothing matches these filters."
          className="sim-table"
        />
      ) : (
        <div className="sim-grid">
          {sims.map((sim) => (
            <button
              key={sim.id}
              // Draft is the students-can't-see-it state, so it dims the whole
              // card rather than only tagging it — scanning a grid, absence of
              // colour reads faster than presence of a small badge.
              className={`sim-card ${draft(sim) ? 'sim-card-unpublished' : ''}`}
              style={{ '--sim-cap': SUBJECT_CAP[sim.subject] } as React.CSSProperties}
              onClick={() => navigate(hrefOf(sim))}
            >
              {/* The subject's place on the pH scale, capping the card — the
                  same cards zitmus draws. */}
              <span className="sim-card-cap" aria-hidden="true" />
              <div className="sim-card-thumb" aria-hidden="true">
                {/* Visual only — the `Draft` chip below carries this for
                    screen readers. */}
                {draft(sim) && (
                  <span className="sim-thumb-badge">Not published</span>
                )}
                {sim.thumbnailUrl ? (
                  <img className="sim-card-img" src={sim.thumbnailUrl} alt="" loading="lazy" />
                ) : sim.equation ? (
                  <span
                    className="sim-card-equation"
                    style={sim.accentColor ? { '--sim-accent': sim.accentColor } as React.CSSProperties : undefined}
                  >{sim.equation}</span>
                ) : (
                  <span className="sim-card-glyph">{sim.mark ? <LabMark lab={sim.mark} size={64} /> : sim.glyph}</span>
                )}
              </div>
              <div className="sim-card-body">
                <div className="sim-card-meta">
                  <span className="sim-card-label">{cardLabel(sim)}</span>
                  {draft(sim) && <span className="sim-card-draft">Draft</span>}
                </div>
                <h3 className="sim-card-title">{sim.title}</h3>
                <p className="sim-card-tagline">{sim.tagline}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
